'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { MemberZoneButton } from './member-zone-button';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { MemberZoneMobileButton } from './member-zone-mobile-button';
import MobileSidebar from './mobile-sidebar';
import { useEffect, useState } from 'react';
import { LanguageSelect } from '@eliadmin/core/src/components/header/language-select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type HeaderMainProps = {
  menu: Menu[];
  generalLayout: GeneralLayout;
};

export function HeaderMain({ menu, generalLayout }: HeaderMainProps) {
  const { t } = useTranslations();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const me = useCustomerStore(s => s.me);

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'fixed left-0 top-0 z-[49] flex w-full flex-col items-center justify-center bg-background bg-white',
          scrolled && 'ea-header-main__scrolled',
          'custom-section',
          'ea-header-main',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'hidden items-center justify-between border-b-black/10 py-4 md:flex md:border-b',
          )}
        >
          <div className="w-[255px] lg:w-[283px]">
            <MemberZoneButton />
          </div>

          <Link prefetchOnHover prefetch={false} href={ROUTES.HOME()}>
            <Image
              src={generalLayout.data.header.logo?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(generalLayout?.data?.header?.logo, "SEDMA")}
              width={0}
              height={0}
              className="h-14 w-auto object-contain"
              sizes="200px"
            />
          </Link>
          <div className="flex items-center justify-end gap-4">
            <Link prefetchOnHover
              prefetch={false}
              href={'/nasa-cinnost#uzitocne-linky'}
              className="text-sm font-semibold tracking-tight text-secondary lg:text-base"
            >
              {t('text2396')}
            </Link>
            <LanguageSelect />
          </div>
        </div>
        <div className={cn('custom-container', 'hidden items-center justify-center gap-0 md:flex')}>
          {menu[0]?.fields?.map(link => (
            <Link prefetchOnHover
              key={link.name}
              prefetch={false}
              className="p-4 text-sm font-semibold tracking-tight text-primary lg:text-base"
              href={getSafeHref(link?.path ?? '')}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Mobile Header */}

        <div className="flex w-full items-center justify-between py-2.5 md:hidden">
          <MobileSidebar menu={menu[0] && menu[0]?.fields} />
          <Link prefetchOnHover prefetch={false} href={ROUTES.HOME()}>
            <Image
              src={generalLayout.data.header.logo?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(generalLayout?.data?.header?.logo, "SEDMA")}
              width={0}
              height={0}
              className="h-10 w-auto object-contain sm:h-14"
              sizes="200px"
            />
          </Link>
          <MemberZoneMobileButton />
        </div>
      </div>
    </MobileSearchProvider>
  );
}
